(function () {
  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  // Mirrors src/sizeSpecs.js's SIZE_SPECS width/height — static Printify
  // blueprint dimensions, not something worth a round-trip to fetch.
  const SIZE_DIMENSIONS = {
    110: { horizontal: { width: 10, height: 7.99 }, vertical: { width: 7.99, height: 10 } },
    252: { horizontal: { width: 13.5, height: 10.59 }, vertical: { width: 10.59, height: 13.5 } },
    520: { horizontal: { width: 20, height: 15.98 }, vertical: { width: 15.98, height: 20 } },
    1014: { horizontal: { width: 28.5, height: 19.02 }, vertical: { width: 19.02, height: 28.5 } },
  };

  // `{{ product | json }}` serializes `featured_image` as a plain URL
  // string (not an { src } object) — this normalizes either shape.
  function imageSrc(image) {
    if (!image) return undefined;
    return typeof image === "string" ? image : image.src;
  }

  function findMatchingVariant(product, selectedOptions) {
    return product.variants.find((variant) => {
      const variantOptions = [variant.option1, variant.option2, variant.option3];
      return selectedOptions.every((value, i) => value === null || variantOptions[i] === value);
    });
  }

  function initProduct(root) {
    const productJsonEl = root.querySelector("[data-product-json]");
    if (!productJsonEl) return;
    const product = JSON.parse(productJsonEl.textContent);

    const variantIdInput = root.querySelector("[data-variant-id-input]");
    const priceEl = root.querySelector("[data-product-price]");
    const dimensionsEl = root.querySelector("[data-product-dimensions]");
    const orientation = product.tags?.includes("orientation:vertical") ? "vertical" : "horizontal";
    const addButton = root.querySelector("[data-add-to-cart-button]");
    const addButtonText = root.querySelector("[data-add-to-cart-text]");
    const optionGroups = root.querySelectorAll("[data-option-index]");
    const mainImage = root.querySelector("[data-product-main-image]");
    const mediaMain = root.querySelector(".product__media-main");
    const prevBtn = root.querySelector("[data-media-prev]");
    const nextBtn = root.querySelector("[data-media-next]");
    const thumbButtons = Array.from(root.querySelectorAll("[data-full-src]"));

    // Fades the main image out, swaps src once it's invisible, fades back
    // in — replaces the old instant `mainImage.src = ...` jump-cut. Reads
    // the new image in the background via a throwaway Image() first so
    // the fade-in doesn't happen before the browser actually has pixels
    // to show (which would otherwise flash blank/broken during a slow
    // load).
    function setMainImage(src) {
      if (!src || src === mainImage.src) return;
      mainImage.classList.add("is-swapping");
      const preloaded = new Image();
      preloaded.onload = () => {
        mainImage.src = src;
        mainImage.classList.remove("is-swapping");
      };
      preloaded.onerror = () => {
        mainImage.src = src;
        mainImage.classList.remove("is-swapping");
      };
      preloaded.src = src;
    }

    function selectThumb(thumb) {
      if (!thumb) return;
      thumbButtons.forEach((t) => t.classList.remove("is-active"));
      thumb.classList.add("is-active");
      setMainImage(thumb.dataset.fullSrc);
    }

    function visibleThumbs() {
      return thumbButtons.filter((t) => !t.hidden);
    }

    // Each photo's piece count is encoded in its filename (`110pc-clean.jpg`,
    // `110pc-mockup-front.jpg`, ...) which Shopify keeps as the src prefix.
    // We group the gallery by that rather than by Shopify variant images,
    // since a Shopify variant can only hold one image (see createProduct.js).
    function piecesFromThumb(thumb) {
      const match = (thumb.dataset.fullSrc || "").match(/(\d+)pc[-.]/);
      return match ? match[1] : null;
    }

    // Show only the selected size's photos. Photos with no piece count in the
    // filename (older pre-per-size products) are shown for every size, so
    // those products still work. Keeps the main image + thumbnails in sync
    // with the chosen piece count, since mockups/flat crops differ per size.
    function syncGalleryForPieces(pieces) {
      if (thumbButtons.length === 0) return;
      let firstVisible = null;
      thumbButtons.forEach((thumb) => {
        const p = piecesFromThumb(thumb);
        const show = p === null || p === String(pieces);
        thumb.hidden = !show;
        if (show && !firstVisible) firstVisible = thumb;
      });
      const active = thumbButtons.find((t) => t.classList.contains("is-active"));
      if (!active || active.hidden) selectThumb(firstVisible);
      const multiple = visibleThumbs().length > 1;
      if (prevBtn) prevBtn.hidden = !multiple;
      if (nextBtn) nextBtn.hidden = !multiple;
    }

    function moveGallery(delta) {
      const visible = visibleThumbs();
      if (visible.length === 0) return;
      const activeIndex = visible.findIndex((t) => t.classList.contains("is-active"));
      const start = activeIndex === -1 ? 0 : activeIndex;
      const nextIndex = (start + delta + visible.length) % visible.length;
      selectThumb(visible[nextIndex]);
    }

    function getSelectedOptions() {
      return Array.from(optionGroups).map((group) => {
        const selected = group.querySelector(".variant-pill.is-selected");
        return selected ? selected.dataset.optionValue : null;
      });
    }

    function updateForVariant() {
      const variant = findMatchingVariant(product, getSelectedOptions());
      if (!variant) return;

      variantIdInput.value = variant.id;
      priceEl.textContent = formatMoney(variant.price);

      const pieces = Number(variant.option1?.replace(" piece", ""));
      const dims = SIZE_DIMENSIONS[pieces]?.[orientation];
      if (dimensionsEl) {
        dimensionsEl.textContent = dims ? `${dims.width}" x ${dims.height}"` : "";
      }

      // Overrides the orientation class's static master-size ratio with the
      // selected size's real print ratio (same center-crop-in-CSS technique
      // as ai-search.js's preview modal) so the photo shown here matches
      // how this size actually crops at print time.
      if (mediaMain) {
        mediaMain.style.aspectRatio = dims ? `${dims.width} / ${dims.height}` : "";
      }

      syncGalleryForPieces(pieces);

      if (variant.available) {
        addButton.disabled = false;
        addButtonText.textContent = window.PieceworkStrings?.product?.addToCart || "Add to cart";
      } else {
        addButton.disabled = true;
        addButtonText.textContent = window.PieceworkStrings?.product?.soldOut || "Sold out";
      }

      const url = new URL(window.location);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }

    optionGroups.forEach((group) => {
      group.querySelectorAll(".variant-pill").forEach((pill) => {
        pill.addEventListener("click", () => {
          group.querySelectorAll(".variant-pill").forEach((p) => p.classList.remove("is-selected"));
          pill.classList.add("is-selected");
          updateForVariant();
        });
      });
    });

    // Price/image are already server-rendered for the initial variant, but
    // dimensions only exist client-side — populate them on load too.
    updateForVariant();

    addButton.addEventListener("click", () => {
      if (!window.PieceworkCart) return;
      const variant = product.variants.find((v) => String(v.id) === String(variantIdInput.value));
      if (!variant || !variant.available) return;
      window.PieceworkCart.addProduct({
        variantId: variant.id,
        title: product.title,
        price: variant.price / 100,
        imageUrl: imageSrc(variant.featured_image) || imageSrc(product.featured_image) || imageSrc(product.images[0]),
        productUrl: `/products/${product.handle}`,
        pieces: parseInt(variant.option1, 10),
      });
      window.PieceworkCart.flashAdded(addButton, window.PieceworkStrings?.product?.added, addButtonText);
    });

    thumbButtons.forEach((thumb) => {
      thumb.addEventListener("click", () => selectThumb(thumb));
    });
    if (prevBtn) prevBtn.addEventListener("click", () => moveGallery(-1));
    if (nextBtn) nextBtn.addEventListener("click", () => moveGallery(1));

    // Click the main image to open a full-size lightbox. Bumps the CDN
    // width param up so the enlarged view is genuinely higher-res than the
    // 1200px gallery image, not just the same pixels scaled up. The overlay
    // is created once, lazily, and shared across opens.
    function hiRes(src) {
      return src ? src.replace(/([?&]width=)\d+/i, (m, p1) => p1 + "2048") : src;
    }
    let lightbox = null;
    function openLightbox(src) {
      if (!src) return;
      if (!lightbox) {
        lightbox = document.createElement("div");
        lightbox.className = "product-lightbox";
        lightbox.innerHTML =
          '<button type="button" class="product-lightbox__close" aria-label="Close">×</button><img alt="">';
        document.body.appendChild(lightbox);
        const close = () => lightbox.classList.remove("is-open");
        lightbox.addEventListener("click", (e) => {
          if (e.target === lightbox || e.target.classList.contains("product-lightbox__close")) close();
        });
        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && lightbox.classList.contains("is-open")) close();
        });
      }
      lightbox.querySelector("img").src = hiRes(src);
      lightbox.classList.add("is-open");
    }
    if (mainImage) {
      mainImage.style.cursor = "zoom-in";
      mainImage.addEventListener("click", () => openLightbox(mainImage.src));
    }
  }

  document.querySelectorAll("[data-product-root]").forEach(initProduct);
})();
